import { useCallback, useMemo, useState } from 'react'
import { Inbox, Plus, TimerReset } from 'lucide-react'

import { PageShell } from '@/components/common/PageShell'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import {
  DataTable,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  DataTableCard,
} from '@/components/common/DataTable'
import { CompanyChips } from '@/features/shifts/components/CompanyChips'
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar'
import { Badge, Select, Button, Modal } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { BadgeVariant, SelectOption } from '@/components/ui'
import { useMyCompanyFilter } from '@/features/companies/hooks/useMyCompanyFilter'
import { useMyTimeCorrections } from '@/features/attendance/hooks/useTimeCorrection'
import { usePermissions } from '@/hooks/usePermissions'
import { formatDateTime } from '@/lib/formatters'
import { CreateWorkspaceTimeCorrectionModal } from './CreateWorkspaceTimeCorrectionModal'
import type {
  MyTimeCorrectionRequestListQuery,
  TimeCorrectionRequest,
  TimeCorrectionRequestStatus,
} from '@/features/attendance/types/timeCorrection.types'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const LIMIT = 10

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

const STATUS_VARIANT: Record<TimeCorrectionRequestStatus, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

function requestSummary(request: TimeCorrectionRequest): string {
  const parts: string[] = []
  if (request.requestedClockIn) parts.push('Clock-in')
  if (request.requestedClockOut) parts.push('Clock-out')
  if (request.requestedBreaks?.length) parts.push('Breaks')
  return parts.length > 0 ? parts.join(', ') : 'Correction'
}

export function MyTimeCorrectionsPage(): React.ReactElement {
  const { can } = usePermissions()
  const [status, setStatus] = useState<TimeCorrectionRequestStatus | ''>('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<TimeCorrectionRequest | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const companyFilter = useMyCompanyFilter({ onChange: () => setPage(1) })
  const { companies: myCompanies, selectedCompanyIds, companyIds, companyNameById } = companyFilter
  const companyIdsArr = companyIds ?? []

  const query = useMemo<MyTimeCorrectionRequestListQuery>(
    () => ({
      ...(companyIds ? { companyIds } : {}),
      ...(status ? { status } : {}),
      page,
      limit: LIMIT,
    }),
    [companyIds, page, status]
  )

  const { data, isLoading, isError, error, refetch } = useMyTimeCorrections(query)
  const requests = data?.data ?? []
  const total = data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / LIMIT))
  const canCreateTimeCorrection = can('time_corrections', 'create_self')
  const defaultCreateCompanyId =
    companyIdsArr.length === 1
      ? companyIdsArr[0]
      : myCompanies.length === 1
        ? myCompanies[0]._id
        : null

  const toggleCompany = (companyId: string | null): void => {
    companyFilter.toggle(companyId)
  }

  const handleRowClick = useCallback((request: TimeCorrectionRequest) => {
    setSelected(request)
  }, [])

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Workspace', to: '/dashboard' }, { label: 'My Time Corrections' }]}
      icon={TimerReset}
      title="My Time Corrections"
      description="Time correction requests you submitted across every company you belong to."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <CompanyChips
              companies={myCompanies}
              selectedCompanyIds={selectedCompanyIds}
              onToggle={toggleCompany}
            />
          }
          actions={
            canCreateTimeCorrection ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setCreateOpen(true)}
                disabled={myCompanies.length === 0}
              >
                <Plus className="h-4 w-4" aria-hidden />
                <span className="ml-1.5">Create Request</span>
              </Button>
            ) : null
          }
        >
          <div className="w-full sm:w-[220px]">
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={(value) => {
                setStatus(value as TimeCorrectionRequestStatus | '')
                setPage(1)
              }}
            />
          </div>
        </WorkspaceToolbar>

        {isLoading ? (
          <DataTableCard>
            <SectionLoader variant="table" cols={5} rows={6} label="Loading time correction requests" />
          </DataTableCard>
        ) : isError ? (
          <DataTableCard>
            <QueryErrorState
              error={error}
              title="Couldn't load your time correction requests"
              onRetry={refetch}
              className="border-0 bg-transparent dark:border-0 dark:bg-transparent"
            />
          </DataTableCard>
        ) : requests.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No time correction requests"
            description="Requests you submit from attendance records will appear here."
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeadCell>Requested</DataTableHeadCell>
                <DataTableHeadCell>Company</DataTableHeadCell>
                <DataTableHeadCell>Changes</DataTableHeadCell>
                <DataTableHeadCell>Status</DataTableHeadCell>
                <DataTableHeadCell align="right">Action</DataTableHeadCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {requests.map((request) => (
                <DataTableRow key={request._id}>
                  <DataTableCell>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {request.createdAt ? formatDateTime(request.createdAt) : '-'}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    {companyNameById.get(request.companyId) ?? '-'}
                  </DataTableCell>
                  <DataTableCell>{requestSummary(request)}</DataTableCell>
                  <DataTableCell>
                    <Badge variant={STATUS_VARIANT[request.status]}>
                      {request.status}
                    </Badge>
                  </DataTableCell>
                  <DataTableCell align="right">
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => handleRowClick(request)}
                    >
                      View
                    </Button>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        <TablePaginationBar
          page={page}
          limit={LIMIT}
          total={total}
          totalPages={totalPages}
          onPageChange={setPage}
        />

        <Modal
          open={selected !== null}
          title="Time correction request"
          onClose={() => setSelected(null)}
          footer={
            <Button type="button" variant="secondary" onClick={() => setSelected(null)}>
              Close
            </Button>
          }
        >
          {selected ? (
            <div className="space-y-4 text-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <Detail label="Company" value={companyNameById.get(selected.companyId) ?? '-'} />
                <Detail label="Status" value={selected.status} />
                <Detail
                  label="Requested"
                  value={selected.createdAt ? formatDateTime(selected.createdAt) : '-'}
                />
                <Detail label="Changes" value={requestSummary(selected)} />
              </div>
              <Detail label="Reason" value={selected.reason} />
              {selected.requestedClockIn ? (
                <Detail label="Requested clock-in" value={formatDateTime(selected.requestedClockIn)} />
              ) : null}
              {selected.requestedClockOut ? (
                <Detail label="Requested clock-out" value={formatDateTime(selected.requestedClockOut)} />
              ) : null}
              {selected.approvedClockIn ? (
                <Detail label="Approved clock-in (adjusted by reviewer)" value={formatDateTime(selected.approvedClockIn)} />
              ) : null}
              {selected.approvedClockOut ? (
                <Detail label="Approved clock-out (adjusted by reviewer)" value={formatDateTime(selected.approvedClockOut)} />
              ) : null}
              {selected.reviewNote ? (
                <Detail label="Reviewer note" value={selected.reviewNote} />
              ) : null}
              {selected.rejectionReason ? (
                <Detail label="Rejection reason" value={selected.rejectionReason} />
              ) : null}
            </div>
          ) : null}
        </Modal>

        <CreateWorkspaceTimeCorrectionModal
          open={createOpen}
          companies={myCompanies}
          defaultCompanyId={defaultCreateCompanyId}
          onClose={() => setCreateOpen(false)}
          onSuccess={() => {
            setCreateOpen(false)
            void refetch()
          }}
        />
      </div>
    </PageShell>
  )
}

function Detail({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-1 text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  )
}

MyTimeCorrectionsPage.displayName = 'MyTimeCorrectionsPage'
